/**
 * Used Car Finance (UCF) Insert API Service
 * Handles used car financing application submission
 *
 * Refactored from: 3in1cms/bajajfinserv-web/ui.solid/src/lib/connectionManager/ucfInsertApi.ts
 * Logic preserved AS-IS, using Redux instead of SolidJS signals
 */

import { COMMON_SSO_ACCESS_TOKEN } from "../constants/sso";
import { logPageActionToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import { getLoginState } from "@repo/shared-utils/insert-params";
import { request } from "../core/request";
import { API_RESPONSE_CODES, BFL_WHEELES_ERROR, OPERATION_IDS } from "./constants";
import type { IUcfInsertApiRequest, IUcfInsertApiResponse } from "../types/ucfInsertTypes";

/**
 * Insert used car finance application
 *
 * @param data - Used car finance application request data
 * @returns Promise with used car finance insert response
 *
 * @example
 * ```typescript
 * const response = await ucfInsert({
 *   mobileNo: "9876543210",
 *   journeyName: "UCF_FRESH",
 *   carMake: "Honda",
 *   carModel: "City",
 *   loanAmount: "300000"
 * });
 * ```
 */
export const ucfInsert = async (data: IUcfInsertApiRequest): Promise<IUcfInsertApiResponse> => {
  try {
    const response = (await request({
      operationId: OPERATION_IDS.ucfInsert,
      requestData: { ...data },
    })) as IUcfInsertApiResponse;

    if (response.statusCode !== API_RESPONSE_CODES.STATUS_CODE_7001) {
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error) {
    logPageActionToNewRelic(BFL_WHEELES_ERROR, {
      component: "Used Car Finance Insert",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      message: (error as Error).message,
      debugInfo: JSON.stringify({
        loginState: getLoginState(),
        hasAToken: typeof window !== "undefined" && !!localStorage.getItem(COMMON_SSO_ACCESS_TOKEN),
        hasMobile: !!data.mobileNo,
      }),
    });
    return {} as IUcfInsertApiResponse;
  }
};
